import {
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import {
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from "firebase/firestore";

import { auth, db, secondaryAuth } from "../firebase/firebase";

const STORAGE_KEY = "ac_admin_user";
const STORED_ACCOUNTS_KEY = "ac_admin_accounts";

function saveUser(user) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(user));
}

function clearUser() {
  localStorage.removeItem(STORAGE_KEY);
}

function readUser() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function normalizeAssignedClasses(adminData = {}) {
  if (Array.isArray(adminData.assignedClasses)) {
    return adminData.assignedClasses
      .map((value) => String(value || "").trim())
      .filter(Boolean);
  }

  if (typeof adminData.assignedClass === "string" && adminData.assignedClass.trim()) {
    return [adminData.assignedClass.trim()];
  }

  return [];
}

function normalizeAdminUser(firebaseUser, adminData = {}) {
  return {
    uid: firebaseUser.uid,
    email: firebaseUser.email || "",
    role: adminData.role || "admin",
    name: adminData.name || "",
    active: adminData.active === true,
    assignedClasses: normalizeAssignedClasses(adminData),
    schoolName: adminData.schoolName || "",
    lastLoginAt: adminData.lastLoginAt || null,
  };
}

async function getActiveAdminByUid(uid) {
  const adminRef = doc(db, "admins", uid);
  const adminSnap = await getDoc(adminRef);

  if (!adminSnap.exists()) {
    return null;
  }

  const adminData = adminSnap.data();

  if (adminData.active !== true) {
    return null;
  }

  return adminData;
}

function normalizeRequestedRole(role) {
  const cleanRole = String(role || "").trim().toLowerCase();

  if (cleanRole === "teacher") return "teacher";
  if (cleanRole === "viewer") return "viewer";
  if (cleanRole === "admin") return "admin";

  return "viewer";
}

function normalizeRequestedClasses(value) {
  if (!Array.isArray(value)) return [];

  return value
    .map((entry) => String(entry || "").trim())
    .filter(Boolean)
    .slice(0, 3);
}

function buildTempPassword() {
  return `AceTime!${Math.random().toString(36).slice(2, 10)}9`;
}

export function getCurrentUser() {
  return readUser();
}

export function getAssignedClasses(user = null) {
  const source = user || readUser();
  if (!source) return [];
  return normalizeAssignedClasses(source);
}

export function isAdminUser(user = null) {
  const source = user || readUser();
  return source?.active === true && source?.role === "admin";
}

export function isTeacherUser(user = null) {
  const source = user || readUser();
  return source?.active === true && source?.role === "teacher";
}

export function isViewerUser(user = null) {
  const source = user || readUser();
  return source?.active === true && source?.role === "viewer";
}

export function isScopedStaffUser(user = null) {
  return isTeacherUser(user) || isViewerUser(user);
}

export function canAccessClassRecord(className, user = null) {
  if (!className) return false;
  if (isAdminUser(user)) return true;

  const normalize = (value) => String(value || "").trim().toLowerCase();
  const allowed = getAssignedClasses(user).map(normalize);

  return allowed.includes(normalize(className));
}

export function canAccessUserRecord(targetUser, user = null) {
  if (!targetUser) return false;
  if (isAdminUser(user)) return true;

  const className =
    targetUser.className ||
    targetUser.class ||
    targetUser.requestedClassLabel ||
    "";

  return canAccessClassRecord(className, user);
}

/**
 * Übergangslösung für bestehende AccountSettingsPage.
 */
export function getStoredAccounts() {
  try {
    const raw = localStorage.getItem(STORED_ACCOUNTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Übergangslösung für bestehende AccountSettingsPage.
 */
export function saveStoredAccounts(accounts) {
  const safeAccounts = Array.isArray(accounts) ? accounts : [];
  localStorage.setItem(STORED_ACCOUNTS_KEY, JSON.stringify(safeAccounts));
}

export async function loginAdmin({ email, password }) {
  try {
    const credential = await signInWithEmailAndPassword(
      auth,
      String(email || "").trim(),
      password
    );

    const adminData = await getActiveAdminByUid(credential.user.uid);

    if (!adminData) {
      await signOut(auth);
      clearUser();
      return {
        ok: false,
        error: "Kein aktiver Admin-Zugriff vorhanden.",
      };
    }

    await updateDoc(doc(db, "admins", credential.user.uid), {
      lastLoginAt: serverTimestamp(),
    }).catch(() => {});

    await setDoc(
      doc(db, "adminSessions", credential.user.uid),
      {
        uid: credential.user.uid,
        email: credential.user.email || "",
        role: adminData.role || "admin",
        name: adminData.name || "",
        active: true,
        assignedClasses: normalizeAssignedClasses(adminData),
        startedAt: serverTimestamp(),
        lastSeenAt: serverTimestamp(),
      },
      { merge: true }
    ).catch(() => {});

    const nextAdminData = {
      ...adminData,
      lastLoginAt: new Date().toISOString(),
    };

    const user = normalizeAdminUser(credential.user, nextAdminData);
    saveUser(user);

    return { ok: true, user };
  } catch (error) {
    let message = "Login fehlgeschlagen.";

    if (error?.code === "auth/invalid-credential") {
      message = "E-Mail oder Passwort ist falsch.";
    } else if (error?.code === "auth/too-many-requests") {
      message = "Zu viele Versuche. Bitte warte kurz.";
    } else if (error?.code === "auth/network-request-failed") {
      message = "Netzwerkfehler. Bitte Verbindung prüfen.";
    } else if (error?.message) {
      message = error.message;
    }

    return { ok: false, error: message };
  }
}

export async function requestPasswordReset(email) {
  const cleanEmail = String(email || "").trim().toLowerCase();

  if (!cleanEmail) {
    return {
      ok: false,
      error: "Bitte eine gültige E-Mail-Adresse eingeben.",
    };
  }

  try {
    await sendPasswordResetEmail(auth, cleanEmail);
    return { ok: true };
  } catch (error) {
    console.error("RESET ERROR:", error);

    let message = "Fehler beim Senden der Zurücksetzen-Mail.";

    if (error?.code === "auth/user-not-found") {
      message = "Für diese E-Mail wurde kein Konto gefunden.";
    } else if (error?.code === "auth/invalid-email") {
      message = "Die E-Mail-Adresse ist ungültig.";
    } else if (error?.code === "auth/network-request-failed") {
      message = "Netzwerkfehler. Bitte Verbindung prüfen.";
    } else if (error?.message) {
      message = error.message;
    }

    return { ok: false, error: message };
  }
}

export async function approveAccessRequestAccount(item, reviewer) {
  try {
    const requestId = String(item?.id || "").trim();
    const email = String(item?.email || "").trim().toLowerCase();
    const name = String(item?.name || "").trim();
    const role = normalizeRequestedRole(item?.role);
    const schoolName = String(item?.schoolName || "").trim();
    const requestedClasses = normalizeRequestedClasses(item?.requestedClasses);

    if (!requestId) {
      return { ok: false, error: "Die Anfrage-ID fehlt." };
    }

    if (!email) {
      return { ok: false, error: "Die Anfrage enthält keine gültige E-Mail." };
    }

    if (!name) {
      return { ok: false, error: "Die Anfrage enthält keinen Namen." };
    }

    if (requestedClasses.length === 0) {
      return {
        ok: false,
        error: "Der Anfrage ist keine gültige Klasse zugewiesen.",
      };
    }

    const requestRef = doc(db, "accessRequests", requestId);
    const existingAuthUid = String(item?.authUid || "").trim();

    let authUid = existingAuthUid || "";
    let createdFresh = false;
    let createdUser = null;

    if (!authUid) {
      const tempPassword = buildTempPassword();

      try {
        const credential = await createUserWithEmailAndPassword(
          secondaryAuth,
          email,
          tempPassword
        );
        createdUser = credential.user;
        authUid = credential.user.uid;
        createdFresh = true;
      } catch (error) {
        if (error?.code === "auth/email-already-in-use") {
          return {
            ok: false,
            error:
              "Für diese E-Mail existiert bereits ein Firebase-Login. Lege in Firestore das passende admins-Dokument mit der richtigen UID an oder speichere die authUid in der Anfrage nach.",
          };
        }

        return {
          ok: false,
          error:
            error?.message ||
            "Das Login-Konto konnte nicht erstellt werden.",
        };
      }
    }

    await setDoc(
      doc(db, "admins", authUid),
      {
        name,
        email,
        role,
        active: true,
        assignedClasses: requestedClasses,
        schoolName,
        createdFromRequestId: requestId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        lastLoginAt: null,
      },
      { merge: true }
    );

    let activationMailSent = false;
    let warning = "";

    try {
      await sendPasswordResetEmail(auth, email);
      activationMailSent = true;
    } catch (mailError) {
      console.error("PASSWORD MAIL ERROR:", mailError);
      warning =
        mailError?.message ||
        "Die Passwort-Mail konnte nicht automatisch gesendet werden.";
    }

    await updateDoc(requestRef, {
      status: "approved",
      reviewedAt: serverTimestamp(),
      reviewedByUid: reviewer?.uid || "",
      reviewedByName: reviewer?.name || reviewer?.email || "",
      authUid,
      onboardingState: "approved",
      passwordSetupStatus: activationMailSent ? "email-sent" : "email-failed",
    });

    await setDoc(
      doc(db, "adminLogs", `access-${requestId}`),
      {
        type: "access-request-approved",
        requestId,
        authUid,
        email,
        name,
        role,
        requestedClasses,
        reviewedAt: serverTimestamp(),
        reviewedByUid: reviewer?.uid || "",
        reviewedByName: reviewer?.name || reviewer?.email || "",
        activationMailSent,
        createdFresh,
      },
      { merge: true }
    );

    try {
      if (createdUser) {
        await signOut(secondaryAuth);
      }
    } catch {}

    return {
      ok: true,
      activationMailSent,
      authUid,
      createdFresh,
      warning,
    };
  } catch (error) {
    console.error("APPROVE ACCESS REQUEST ERROR:", error);

    return {
      ok: false,
      error: error?.message || "Die Anfrage konnte nicht genehmigt werden.",
    };
  }
}

export async function logoutAdmin() {
  try {
    const current = auth.currentUser;
    if (current?.uid) {
      await updateDoc(doc(db, "adminSessions", current.uid), {
        endedAt: serverTimestamp(),
        lastSeenAt: serverTimestamp(),
      }).catch(() => {});
    }
  } catch {}

  await signOut(auth);
  clearUser();
}

export function subscribeToAdminAuth(callback) {
  return onAuthStateChanged(auth, async (firebaseUser) => {
    if (!firebaseUser) {
      clearUser();
      callback(null);
      return;
    }

    try {
      const adminData = await getActiveAdminByUid(firebaseUser.uid);

      if (!adminData) {
        await signOut(auth);
        clearUser();
        callback(null);
        return;
      }

      await setDoc(
        doc(db, "adminSessions", firebaseUser.uid),
        {
          uid: firebaseUser.uid,
          email: firebaseUser.email || "",
          role: adminData.role || "admin",
          name: adminData.name || "",
          active: true,
          assignedClasses: normalizeAssignedClasses(adminData),
          lastSeenAt: serverTimestamp(),
        },
        { merge: true }
      ).catch(() => {});

      const user = normalizeAdminUser(firebaseUser, adminData);
      saveUser(user);
      callback(user);
    } catch (error) {
      console.error("SUBSCRIBE AUTH ERROR:", error);
      clearUser();
      callback(null);
    }
  });
}